/**
 * CMS-04 — song editor (a page, not a drawer: it carries five panels).
 *
 * Left column: song data (title, ordered artists, spotify id, relevance, tier, language, genre,
 * status), lyrics + LRC, and the tones table. Right column: image + the read-only external ids
 * from `tools/catalog-identify` with the "use the Deezer cover" action, the MP4 media check
 * (docs/07 §4 — there is no HLS package to verify in phase 1) and the source files.
 */
import type {
  ArtistRef,
  CmsSongDTO,
  CmsSongPatchBodyInput,
  CmsSongResponse,
  CmsTier,
  CmsToneDTO,
  SongLanguage,
  SongMediaCheckReport,
  SongStatus,
} from '@akpl/shared';
import { isAkpError } from '@akpl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ChangeEvent, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { ArtistSelect } from '../components/catalog/ArtistSelect';
import { StatusBadge } from '../components/catalog/badges';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Field, Input, Select, Textarea } from '../components/ui/field';
import { Badge, Panel, PanelHeader } from '../components/ui/panel';
import { useToast } from '../components/ui/toast';
import { useT } from '../i18n';
import { formatDateTime } from '../lib/format';
import { uploadFile } from '../lib/upload';

interface Draft {
  title: string;
  artists: ArtistRef[];
  spotifyId: string;
  relevance: number;
  tier: CmsTier;
  language: SongLanguage | '';
  genre: string;
  status: Exclude<SongStatus, 'published'> | 'published';
  lyricsText: string;
  imagePath: string | null;
  lyricsLrcPath: string | null;
}

function toDraft(song: CmsSongDTO): Draft {
  return {
    title: song.title,
    artists: song.artists,
    spotifyId: song.spotifyId ?? '',
    relevance: song.relevance,
    tier: song.tier === 'recent' ? 'recent' : 'standard',
    language: song.language ?? '',
    genre: song.genre ?? '',
    status: song.status,
    lyricsText: song.lyricsText ?? '',
    imagePath: song.imagePath,
    lyricsLrcPath: song.lyricsLrcPath,
  };
}

/** Only the fields that actually changed travel in the PATCH (docs/06 §4). */
export function songPatch(song: CmsSongDTO, draft: Draft): CmsSongPatchBodyInput {
  const patch: CmsSongPatchBodyInput = {};
  if (draft.title !== song.title) patch.title = draft.title;
  const artistIds = draft.artists.map((a) => a.id);
  if (artistIds.join('|') !== song.artists.map((a) => a.id).join('|')) patch.artistIds = artistIds;
  if ((draft.spotifyId || null) !== song.spotifyId) patch.spotifyId = draft.spotifyId || null;
  if (draft.relevance !== song.relevance) patch.relevance = draft.relevance;
  if (draft.tier !== song.tier) patch.tier = draft.tier;
  if ((draft.language || null) !== song.language) patch.language = draft.language || null;
  if ((draft.genre || null) !== song.genre) patch.genre = draft.genre || null;
  if ((draft.lyricsText || null) !== song.lyricsText) patch.lyricsText = draft.lyricsText || null;
  if (draft.imagePath !== song.imagePath) patch.imagePath = draft.imagePath;
  if (draft.lyricsLrcPath !== song.lyricsLrcPath) patch.lyricsLrcPath = draft.lyricsLrcPath;
  // `published` is owned by publish/unpublish, never by PATCH (docs/03 §2).
  if (draft.status !== song.status && draft.status !== 'published') patch.status = draft.status;
  return patch;
}

/** First lines of an .lrc, the preview CMS-04 shows next to the plain lyrics. */
export function previewLrc(text: string, lines = 4): string[] {
  return text
    .split(/\r?\n/)
    .filter((line) => /^\[\d{2}:\d{2}[.:]\d{2}\]/.test(line))
    .slice(0, lines);
}

function ExternalIds({ song }: { song: CmsSongDTO }) {
  const rows: Array<[string, string]> = [
    ['ISRC', song.external.isrc ?? '—'],
    ['MusicBrainz', song.external.mbid ?? '—'],
    ['Deezer id', song.external.deezerId === null ? '—' : String(song.external.deezerId)],
    ['artist_mbids', (song.external.artistMbids ?? []).join(', ') || '—'],
  ];
  return (
    <dl className="mt-3 text-[12px]">
      {rows.map(([key, value]) => (
        <div key={key} className="flex gap-3 border-t border-line py-1.5 first:border-0">
          <dt className="w-24 shrink-0 text-muted">{key}</dt>
          <dd className="min-w-0 flex-1 break-all text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function TonesPanel({ song, canEdit }: { song: CmsSongDTO; canEdit: boolean }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [tones, setTones] = useState<CmsToneDTO[]>(song.tones);

  const save = useMutation({
    mutationFn: () =>
      api.cmsSongTonesPut({
        params: { id: song.id },
        body: {
          tones: tones.map((tone) => ({
            name: tone.name,
            label: tone.label,
            semitones: tone.semitones,
            isDefault: tone.isDefault,
          })),
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cms', 'song', song.id] }),
  });

  const patchTone = (index: number, patch: Partial<CmsToneDTO>) =>
    setTones((list) => list.map((tone, i) => (i === index ? { ...tone, ...patch } : tone)));

  const addTone = () => {
    const used = new Set(tones.map((tone) => tone.name));
    const next = ['-2', '-1', '+1', '+2'].find((name) => !used.has(name)) ?? '0';
    setTones((list) => [
      ...list,
      {
        name: next,
        label: { en: `${next} semitones`, es: `${next} semitonos` },
        semitones: Number(next),
        isDefault: false,
        position: list.length,
      },
    ]);
  };

  return (
    <Panel>
      <PanelHeader
        title={t('cms:songs.editor.tones')}
        subtitle={t('cms:songs.editor.tonesHelp')}
        action={
          canEdit ? (
            <Button variant="secondary" size="sm" onClick={addTone}>
              + {t('cms:songs.editor.addTone')}
            </Button>
          ) : null
        }
      />
      <div className="overflow-x-auto px-4 py-3">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="text-[12px] tracking-wide text-muted uppercase">
              <th scope="col" className="py-1.5 pr-3">
                {t('cms:songs.tones.name')}
              </th>
              <th scope="col" className="py-1.5 pr-3">
                {t('cms:songs.tones.labelEs')} / {t('cms:songs.tones.labelEn')}
              </th>
              <th scope="col" className="py-1.5 pr-3">
                {t('cms:songs.tones.semitones')}
              </th>
              <th scope="col" className="py-1.5 pr-3">
                {t('cms:songs.tones.default')}
              </th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {tones.map((tone, index) => (
              <tr key={`${tone.name}-${index}`} className="border-t border-line">
                <td className="py-2 pr-3 font-semibold">
                  <Input
                    className="h-8 w-16"
                    aria-label={`${t('cms:songs.tones.name')} ${index + 1}`}
                    value={tone.name}
                    disabled={!canEdit}
                    onChange={(e) =>
                      patchTone(index, {
                        name: e.target.value,
                        semitones: Number(e.target.value) || 0,
                      })
                    }
                  />
                </td>
                <td className="py-2 pr-3">
                  <Input
                    className="h-8"
                    aria-label={`${t('cms:songs.tones.labelEs')} ${index + 1}`}
                    value={tone.label.es}
                    disabled={!canEdit}
                    onChange={(e) =>
                      patchTone(index, { label: { ...tone.label, es: e.target.value } })
                    }
                  />
                </td>
                <td className="py-2 pr-3 tabular-nums">{tone.semitones}</td>
                <td className="py-2 pr-3">
                  <input
                    type="radio"
                    name="default-tone"
                    aria-label={`${t('cms:songs.tones.default')} ${tone.name}`}
                    checked={tone.isDefault}
                    disabled={!canEdit}
                    onChange={() =>
                      setTones((list) => list.map((x, i) => ({ ...x, isDefault: i === index })))
                    }
                  />
                </td>
                <td className="py-2 text-right">
                  {canEdit && tones.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`${t('common:actions.remove')} ${tone.name}`}
                      onClick={() => setTones((list) => list.filter((_, i) => i !== index))}
                    >
                      ✕
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
              {t('cms:songs.editor.saveTones')}
            </Button>
            {save.isError && (
              <span className="text-[12px] text-danger" role="alert">
                {t('common:states.error')}
              </span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

function MediaPanel({ song, canEdit }: { song: CmsSongDTO; canEdit: boolean }) {
  const { t, i18n } = useT();
  const queryClient = useQueryClient();
  const [report, setReport] = useState<SongMediaCheckReport | null>(
    (song.packageReport as SongMediaCheckReport | null) ?? null,
  );

  const check = useMutation({
    mutationFn: () => api.cmsSongMediaCheck({ params: { id: song.id } }),
    onSuccess: (res) => {
      setReport(res.report);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'song', song.id] });
    },
  });

  return (
    <Panel>
      <PanelHeader
        title={t('cms:songs.editor.media')}
        subtitle={t('cms:songs.editor.mediaHelp')}
        action={
          canEdit ? (
            <Button
              variant="link"
              size="sm"
              disabled={check.isPending}
              onClick={() => check.mutate()}
            >
              {t('cms:songs.editor.check')}
            </Button>
          ) : null
        }
      />
      <div className="px-4 py-3 text-[12px]">
        <p className="font-semibold text-ink">{song.sourcePath ?? '—'}</p>
        {report ? (
          <div
            className={`mt-2 rounded-lg px-3 py-2 ${report.ok ? 'bg-ok/10' : 'bg-danger/10'}`}
            data-testid="media-report"
          >
            <p className={report.ok ? 'text-ok-ink' : 'text-danger-ink'}>
              {report.found
                ? `${Math.round((report.size ?? 0) / 1_048_576)} MB · ${
                    report.durationMs ? Math.round(report.durationMs / 1000) : '—'
                  } s · ${report.contentType ?? '—'}`
                : t('cms:songs.editor.mediaMissing')}
            </p>
            <p className="mt-0.5 text-muted">
              {t('cms:songs.editor.checked', {
                time: formatDateTime(report.checkedAt, i18n.language),
              })}
              {report.warnings.length > 0 ? ` · ${report.warnings.join(', ')}` : ''}
            </p>
          </div>
        ) : (
          <p className="mt-2 text-muted">{t('cms:songs.editor.notChecked')}</p>
        )}
        <p className="mt-3 text-muted">{t('cms:songs.editor.drmNote')}</p>
      </div>
    </Panel>
  );
}

/**
 * Route component: loads the song and mounts the form keyed by the saved version, so the draft
 * is re-derived from the server row instead of being synced with an effect.
 */
export function SongEditor({ songId }: { songId: string }) {
  const { t } = useT();
  const { role } = useAuth();
  const result = useQuery<CmsSongResponse>({
    queryKey: ['cms', 'song', songId],
    queryFn: () => api.cmsSong({ params: { id: songId } }),
  });
  const song = result.data?.song;

  /**
   * QA 2026-09-15 **UX-539**: the editor rendered the loading state whenever `data` was
   * undefined, so `GET /v1/cms/songs/<bad id>` → 404 left a stale bookmark or a wrong paste on
   * the single word «Cargando…» for ever — no error, no "no existe", no way back. Every list
   * screen already had a `ListError`; this was the one detail screen with no error branch.
   */
  if (result.isError) {
    const notFound = isAkpError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader
          title={notFound ? t('cms:songs.editor.notFound') : t('cms:songs.editor.loadFailed')}
          subtitle={notFound ? t('cms:songs.editor.notFoundBody', { id: songId }) : undefined}
          actions={
            <span className="flex items-center gap-2">
              {!notFound && (
                <Button variant="secondary" onClick={() => void result.refetch()}>
                  {t('common:actions.retry')}
                </Button>
              )}
              <Button asChild variant="secondary">
                <Link to="/songs">{t('cms:songs.editor.backToSongs')}</Link>
              </Button>
            </span>
          }
        />
        <p className="px-6 pb-8 text-[13px] text-muted" role="alert" data-testid="song-not-found">
          {notFound ? t('cms:songs.editor.notFoundBody', { id: songId }) : t('common:states.error')}
        </p>
      </>
    );
  }
  if (!song) return <PageHeader title={t('common:states.loading')} />;
  return (
    <SongEditorForm
      key={`${song.id}:${song.updatedAt}`}
      song={song}
      canEdit={atLeast(role, 'editor')}
    />
  );
}

function SongEditorForm({ song, canEdit }: { song: CmsSongDTO; canEdit: boolean }) {
  const { t, i18n } = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const songId = song.id;

  const confirm = useConfirm();
  const toast = useToast();

  const [draft, setDraft] = useState<Draft>(() => toDraft(song));
  const [saved, setSaved] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const lrcInput = useRef<HTMLInputElement>(null);
  const [lrcPreview, setLrcPreview] = useState<string[]>([]);

  const patch = useMemo(() => songPatch(song, draft), [song, draft]);
  const dirty = Object.keys(patch).length > 0;

  const save = useMutation({
    mutationFn: () => api.cmsSongPatch({ params: { id: songId }, body: patch }),
    onSuccess: (res) => {
      queryClient.setQueryData(['cms', 'song', songId], res);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'songs'] });
      setSaved(true);
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await api.cmsSongPatch({ params: { id: songId }, body: patch });
      return api.cmsSongPublish({ params: { id: songId } });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(['cms', 'song', songId], res);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'songs'] });
    },
  });

  /**
   * The walk-back of an unpublish (UX-211): a plain republish, no PATCH, no confirmation — it
   * puts the catalog back exactly where it was. It is its own mutation so the undo cannot drag
   * an unsaved draft along with it the way `publish` deliberately does.
   */
  const republish = useMutation({
    mutationFn: () => api.cmsSongPublish({ params: { id: songId } }),
    onSuccess: (res) => {
      queryClient.setQueryData(['cms', 'song', songId], res);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'songs'] });
      toast({ message: t('cms:undo.songRepublished', { name: song.title }) });
    },
    onError: () => toast({ message: t('cms:undo.failed'), tone: 'danger' }),
  });

  const unpublish = useMutation({
    mutationFn: () => api.cmsSongUnpublish({ params: { id: songId } }),
    onSuccess: (res) => {
      queryClient.setQueryData(['cms', 'song', songId], res);
      void queryClient.invalidateQueries({ queryKey: ['cms', 'songs'] });
      // The song is out of the live catalog: say so, and keep the way back one tab stop away
      // for UNDO_MS (docs/06 §1.4).
      toast({
        message: t('cms:undo.songUnpublished', { name: song.title }),
        action: { label: t('cms:undo.action'), onAction: () => republish.mutate() },
      });
    },
  });

  const deezerCover = useMutation({
    mutationFn: () => api.cmsSongDeezerCover({ params: { id: songId } }),
    onSuccess: (res) => queryClient.setQueryData(['cms', 'song', songId], res),
  });

  const remove = useMutation({
    mutationFn: () => api.cmsSongDelete({ params: { id: songId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'songs'] });
      void navigate({ to: '/songs' });
    },
  });

  const set = (piece: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...piece }));
    setSaved(false);
  };

  const onImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    try {
      const { key } = await uploadFile(file, { kind: 'image', owner: 'song', ownerId: song.id });
      set({ imagePath: key });
    } catch {
      setUploadError(t('cms:songs.editor.uploadFailed'));
    }
  };

  const onLrc = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    try {
      setLrcPreview(previewLrc(await file.text()));
      const { key } = await uploadFile(file, { kind: 'lrc', songId: song.id });
      set({ lyricsLrcPath: key });
    } catch {
      setUploadError(t('cms:songs.editor.uploadFailed'));
    }
  };

  /**
   * UX-211: publish and unpublish both change what all 143,392-song rooms can see, so both ask
   * first. Unpublish is the destructive one (the song leaves the live catalog), publish is
   * neutral, so they open on different default buttons.
   */
  const onUnpublish = async () => {
    const ok = await confirm({
      title: t('cms:confirm.unpublishSong.title', { name: song.title }),
      body: t('cms:confirm.unpublishSong.body'),
      confirmLabel: t('cms:confirm.unpublishSong.confirm'),
      tone: 'danger',
    });
    if (ok) unpublish.mutate();
  };

  const onPublish = async () => {
    const ok = await confirm({
      title: t('cms:confirm.publishSong.title', { name: song.title }),
      body: t('cms:confirm.publishSong.body'),
      confirmLabel: t('cms:confirm.publishSong.confirm'),
      tone: 'primary',
    });
    if (ok) publish.mutate();
  };

  const onDelete = async () => {
    // docs/06 §4: deleting a song asks for the name to be typed.
    const ok = await confirm({
      title: t('cms:confirm.deleteSong.title', { name: song.title }),
      body: t('cms:confirm.deleteSong.body'),
      confirmLabel: t('cms:confirm.deleteSong.confirm'),
      tone: 'danger',
      requireText: song.title,
      requireTextHelp: t('cms:actions.confirmDelete', { name: song.title }),
    });
    if (ok) remove.mutate();
  };

  return (
    <>
      <PageHeader
        title={song.title}
        subtitle={
          <>
            <Link to="/songs" className="text-pink hover:underline">
              {t('cms:songs.editor.back')}
            </Link>{' '}
            /{' '}
            {t('cms:songs.editor.meta', {
              id: song.sourceId ?? song.id,
              source: song.sourcePath ?? '—',
              status: t(`cms:status.${song.status}` as 'cms:status.draft'),
            })}
          </>
        }
        actions={
          canEdit ? (
            <>
              {song.status === 'published' ? (
                <Button
                  variant="danger"
                  disabled={unpublish.isPending}
                  onClick={() => void onUnpublish()}
                >
                  {t('cms:actions.unpublish')}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? t('common:states.saving') : t('cms:songs.editor.save')}
              </Button>
              {/* UX-511: a published song showed «Despublicar | Guardar | Publicar» and the
                  third button re-asked «¿Publicar …?» about a song that already is. */}
              {song.status !== 'published' && (
                <Button disabled={publish.isPending} onClick={() => void onPublish()}>
                  {t('cms:actions.publish')}
                </Button>
              )}
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-4 px-6 pb-10 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex flex-col gap-4">
          <Panel>
            <PanelHeader
              title={t('cms:songs.editor.data')}
              subtitle={t('cms:songs.editor.dataHelp')}
              action={<StatusBadge status={song.status} />}
            />
            <div className="flex flex-col gap-3 px-4 py-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1.4fr_0.8fr]">
                <Field label={t('cms:songs.fields.title')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={draft.title}
                      disabled={!canEdit}
                      onChange={(e) => set({ title: e.target.value })}
                    />
                  )}
                </Field>
                <Field label={t('cms:songs.fields.spotifyId')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={draft.spotifyId}
                      disabled={!canEdit}
                      onChange={(e) => set({ spotifyId: e.target.value })}
                    />
                  )}
                </Field>
                <Field label={t('cms:songs.fields.relevance')}>
                  {(id) => (
                    <Input
                      id={id}
                      type="number"
                      min={0}
                      value={draft.relevance}
                      disabled={!canEdit}
                      onChange={(e) => set({ relevance: Number(e.target.value) || 0 })}
                    />
                  )}
                </Field>
              </div>

              <ArtistSelect
                value={draft.artists}
                disabled={!canEdit}
                onChange={(artists) => set({ artists })}
              />

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label={t('cms:songs.fields.tier')}>
                  {(id) => (
                    <Select
                      id={id}
                      value={draft.tier}
                      disabled={!canEdit}
                      onChange={(e) => set({ tier: e.target.value as CmsTier })}
                    >
                      {(['standard', 'recent'] as const).map((tier) => (
                        <option key={tier} value={tier}>
                          {t(`common:tier.${tier}` as 'common:tier.standard')}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t('cms:songs.filters.language')}>
                  {(id) => (
                    <Select
                      id={id}
                      value={draft.language}
                      disabled={!canEdit}
                      onChange={(e) => set({ language: e.target.value as SongLanguage | '' })}
                    >
                      <option value="">—</option>
                      {(['es', 'en', 'pt'] as const).map((code) => (
                        <option key={code} value={code}>
                          {t(`cms:songs.languages.${code}` as 'cms:songs.languages.es')}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t('cms:songs.filters.genre')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={draft.genre}
                      disabled={!canEdit}
                      onChange={(e) => set({ genre: e.target.value })}
                    />
                  )}
                </Field>
                <Field label={t('cms:songs.fields.status')}>
                  {(id) => (
                    <Select
                      id={id}
                      value={draft.status}
                      disabled={!canEdit || song.status === 'published'}
                      onChange={(e) => set({ status: e.target.value as Draft['status'] })}
                    >
                      {(song.status === 'published'
                        ? (['published'] as const)
                        : (['draft', 'ready', 'archived'] as const)
                      ).map((status) => (
                        <option key={status} value={status}>
                          {t(`cms:status.${status}` as 'cms:status.draft')}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              {saved && !dirty && (
                <p className="text-[12px] text-ok" role="status">
                  {t('cms:songs.editor.saved')}
                </p>
              )}
              {(save.isError || publish.isError) && (
                <p className="text-[12px] text-danger" role="alert">
                  {t('common:states.error')}
                </p>
              )}
            </div>
          </Panel>

          <Panel>
            <PanelHeader
              title={t('cms:songs.editor.lyrics')}
              subtitle={t('cms:songs.editor.lyricsHelp')}
              action={
                canEdit ? (
                  <>
                    <input
                      ref={lrcInput}
                      type="file"
                      accept=".lrc,text/plain"
                      hidden
                      className="hidden"
                      data-testid="lrc-input"
                      onChange={(event) => void onLrc(event)}
                    />
                    <Button variant="secondary" size="sm" onClick={() => lrcInput.current?.click()}>
                      {t('cms:songs.editor.uploadLrc')}
                    </Button>
                  </>
                ) : null
              }
            />
            <div className="grid grid-cols-1 gap-3 px-4 py-4 lg:grid-cols-2">
              <Textarea
                className="min-h-32"
                aria-label={t('cms:songs.fields.lyrics')}
                value={draft.lyricsText}
                disabled={!canEdit}
                onChange={(e) => set({ lyricsText: e.target.value })}
              />
              <div className="rounded-lg border border-line px-3 py-2 text-[12px]">
                {draft.lyricsLrcPath ? (
                  <>
                    <p className="font-semibold text-ok">
                      {t('cms:songs.editor.lrcStored', { path: draft.lyricsLrcPath })}
                    </p>
                    <ul className="mt-1.5 text-muted">
                      {lrcPreview.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-muted">{t('cms:songs.editor.noLrc')}</p>
                )}
              </div>
            </div>
          </Panel>

          <TonesPanel song={song} canEdit={canEdit} />
        </div>

        <div className="flex flex-col gap-4">
          <Panel>
            <PanelHeader
              title={t('cms:songs.editor.image')}
              subtitle={t('cms:songs.editor.externalHelp')}
              action={
                <Badge
                  tone={song.external.matchStatus === 'matched' ? 'ok' : 'warn'}
                  data-testid="match-badge"
                >
                  {song.external.matchStatus
                    ? t(`cms:songs.match.${song.external.matchStatus}` as 'cms:songs.match.matched')
                    : t('cms:songs.match.none')}
                </Badge>
              }
            />
            <div className="px-4 py-4">
              <div className="flex gap-3">
                {/*
                  Only our own art (R2 `cover_key`/`image_path`, served from media.acapella.lol).
                  `external.coverUrl` is Deezer's CDN: it is provenance shown as text and the
                  source of "Usar carátula", never an <img src> — the clients do not hotlink a
                  third party and the CSP's img-src does not list it (QA3 CMS-64).
                */}
                {song.imageUrl ? (
                  <img src={song.imageUrl} alt="" className="size-20 rounded-lg object-cover" />
                ) : (
                  <span aria-hidden="true" className="size-20 rounded-lg bg-canvas" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-ink">
                    {song.external.coverUrl
                      ? t('cms:songs.editor.coverSuggested')
                      : t('cms:songs.editor.noCover')}
                  </p>
                  <p className="truncate text-[12px] text-muted">{song.external.coverUrl ?? '—'}</p>
                  {canEdit && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        disabled={!song.external.coverUrl || deezerCover.isPending}
                        onClick={() => deezerCover.mutate()}
                      >
                        {t('cms:songs.editor.useCover')}
                      </Button>
                      <input
                        ref={imageInput}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        hidden
                        className="hidden"
                        data-testid="image-input"
                        onChange={(event) => void onImage(event)}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => imageInput.current?.click()}
                      >
                        {t('cms:songs.editor.uploadImage')}
                      </Button>
                    </div>
                  )}
                  {uploadError && (
                    <p className="mt-1 text-[12px] text-danger" role="alert">
                      {uploadError}
                    </p>
                  )}
                  {deezerCover.isError && (
                    <p className="mt-1 text-[12px] text-danger" role="alert">
                      {t('common:states.error')}
                    </p>
                  )}
                </div>
              </div>
              <ExternalIds song={song} />
              {song.external.matchScore !== null && (
                <p className="mt-2 text-[12px] text-muted">
                  {t('cms:songs.match.score', {
                    score: song.external.matchScore,
                    source: song.external.matchSource ?? '—',
                  })}
                </p>
              )}
            </div>
          </Panel>

          <MediaPanel song={song} canEdit={canEdit} />

          {canEdit && (
            <Panel>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <p className="text-[12px] text-muted">
                  {formatDateTime(song.updatedAt, i18n.language)}
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => void onDelete()}
                >
                  {t('cms:songs.editor.delete')}
                </Button>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
